import React, { useEffect, useState } from 'react';
import type { ReviewDecision } from '../../lib/adminReviewApi';

export interface DecisionFormValues {
  reviewer: string;
  review_notes_internal: string;
  customer_safe_summary: string;
  hold_reason: string;
}

interface AdminReviewDecisionModalProps {
  open: boolean;
  decision: ReviewDecision | null;
  uploadId?: string;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (values: DecisionFormValues) => void;
}

const DECISION_LABELS: Record<ReviewDecision, string> = {
  approve: 'Approve',
  approve_with_note: 'Approve With Note',
  hold: 'Hold',
  retry_required: 'Retry Required',
  fail: 'Fail',
};

const AdminReviewDecisionModal: React.FC<AdminReviewDecisionModalProps> = ({
  open,
  decision,
  uploadId,
  submitting,
  onClose,
  onSubmit,
}) => {
  const [reviewer, setReviewer] = useState('');
  const [reviewNotesInternal, setReviewNotesInternal] = useState('');
  const [customerSafeSummary, setCustomerSafeSummary] = useState('');
  const [holdReason, setHoldReason] = useState('');

  useEffect(() => {
    if (open) {
      setReviewer('');
      setReviewNotesInternal('');
      setCustomerSafeSummary('');
      setHoldReason('');
    }
  }, [open, decision, uploadId]);

  if (!open || !decision) {
    return null;
  }

  const requiresNotes =
    decision === 'approve_with_note' ||
    decision === 'hold' ||
    decision === 'retry_required' ||
    decision === 'fail';

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!reviewer.trim()) return;
    onSubmit({
      reviewer: reviewer.trim(),
      review_notes_internal: reviewNotesInternal.trim(),
      customer_safe_summary: customerSafeSummary.trim(),
      hold_reason: holdReason.trim(),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl dark:bg-secondary-900"
      >
        <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
          {DECISION_LABELS[decision]}
        </h2>
        {uploadId ? (
          <p className="mt-1 font-mono text-xs text-secondary-500">{uploadId}</p>
        ) : null}

        <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="mb-1 block text-sm text-secondary-600 dark:text-secondary-300">
              Reviewer name *
            </label>
            <input
              type="text"
              required
              value={reviewer}
              onChange={(e) => setReviewer(e.target.value)}
              className="input-field"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm text-secondary-600 dark:text-secondary-300">
              Internal notes{requiresNotes ? ' *' : ''}
              <span className="ml-2 text-xs text-amber-700 dark:text-amber-300">
                Internal only — do not send to customer
              </span>
            </label>
            <textarea
              required={requiresNotes}
              rows={3}
              value={reviewNotesInternal}
              onChange={(e) => setReviewNotesInternal(e.target.value)}
              className="input-field"
            />
          </div>

          {(decision === 'approve' || decision === 'approve_with_note') && (
            <div>
              <label className="mb-1 block text-sm text-secondary-600 dark:text-secondary-300">
                Customer-safe summary
              </label>
              <textarea
                rows={2}
                value={customerSafeSummary}
                onChange={(e) => setCustomerSafeSummary(e.target.value)}
                className="input-field"
              />
            </div>
          )}

          {(decision === 'hold' || decision === 'retry_required' || decision === 'fail') && (
            <div>
              <label className="mb-1 block text-sm text-secondary-600 dark:text-secondary-300">
                Hold / failure reason
              </label>
              <input
                type="text"
                value={holdReason}
                onChange={(e) => setHoldReason(e.target.value)}
                className="input-field"
              />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn-primary disabled:opacity-50" disabled={submitting}>
              {submitting ? 'Saving…' : 'Submit decision'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminReviewDecisionModal;
